import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import moment from 'moment';
import { ApiResponse, ApiDataResponse, ReportGenerationApiResponse } from '../../../shared/model/api-response.model';
import { Observable } from 'rxjs';
import { GetInvoiceNotes_Response } from '../../../type-definitions/billing/responses/billing';
import { VistaCustomField } from '../../../type-definitions/general/general';

import { SimpleLookupResult } from '../../../type-definitions/general/general';
import { POGetCompanies_Result, POGetRequisitionGrid_Result } from '../../../type-definitions/field/responses/field';
import { POReqInsertUpdateItemsResult } from '../../../type-definitions/field-service/responses/field-service';
import { POReqInsertUpdateItemsRequest } from '../../../type-definitions/field-service/requests/field-service';

@Injectable({
  providedIn: 'root',
})
export class FieldService {
  http: HttpClient;

  constructor(http: HttpClient) {
    this.http = http;
  }

  getCompanies() {
    return this.http.get('/Field/GetJBCompanies').toPromise();
  }

  getContracts(hQCo: number, form: string) {
    return this.http.get('/Field/GetContracts?hQCo=' + hQCo + '&form=' + form).toPromise();
  }

  getContractByjCJMKeyID(LastUsedJCJMKeyID: number) {
    return this.http.get('/Field/GetContract?jCJMKeyID=' + LastUsedJCJMKeyID).toPromise();
  }

  getBills(hQCo: number, contract: string) {
    return this.http.get('/Field/JBGetBills?hQCo=' + hQCo + '&contract=' + contract).toPromise();
  }

  getHuddleTypes() {
    const url = '/Field/GetHuddleTypes';
    return this.http.get(url).toPromise();
  }

  addEditHuddleType(huddleTypeID?: number, huddleTypeName?: string) {
    return this.http
      .post('/Field/AddEditHuddleType', {
        huddleTypeID: huddleTypeID,
        huddleTypeName: huddleTypeName,
      })
      .toPromise();
  }

  getHuddlesToSchedule() {
    const url = '/Field/GetHuddlesToSchedule';
    return this.http.get(url).toPromise();
  }

  removeHuddleType(huddleTypeID?: number) {
    return this.http
      .post('/Field/RemoveHuddleType', {
        huddleTypeID: huddleTypeID,
      })
      .toPromise();
  }

  addEditHuddle(requestObj: any) {
    return this.http
      .post('/Field/AddEditHuddle', {
        huddleTypeID: requestObj.huddleTypeID,
        huddleDateStr: requestObj.huddleDateStr,
        huddleLocation: requestObj.huddleLocation,
        ledByUserKeyID: requestObj.ledByUserKeyID,
        jCJMKeyID: requestObj.jCJMKeyID,
        huddleID: requestObj.huddleID,
        releventFromStr: requestObj.releventFromStr,
        releventToStr: requestObj.releventToStr,
        isTemplateMeeting: requestObj.isTemplateMeeting,
        huddleName: requestObj.huddleName,
        isPublished: requestObj.isPublished,
        startTimeStr: requestObj.startTimeStr,
        notes: requestObj.notes,
      })
      .toPromise();
  }

  initializeTemplateMeeting(templateHuddleID?: number, huddleDateStr?: string) {
    return this.http
      .post('/Field/InitializeTemplateMeeting', {
        templateHuddleID: templateHuddleID,
        huddleDateStr: huddleDateStr,
      })
      .toPromise();
  }

  getHuddles(requestObj: any) {
    const url =
      '/Field/GetHuddles?huddleID=' +
      requestObj.huddleID +
      '&dateFromStr=' +
      requestObj.dateFromStr +
      '&dateToStr=' +
      requestObj.dateToStr +
      '&templateMode=' +
      requestObj.templateMode +
      '&branchID=' +
      requestObj.branchID +
      '&jCJMKeyID=' +
      requestObj.jCJMKeyID +
      '&workTypeID=' +
      requestObj.workTypeID +
      '&phaseTypeID=' +
      requestObj.phaseTypeID;
    return this.http.get(url).toPromise();
  }

  getWorkTypes() {
    const url = '/Field/GetWorkTypes';
    return this.http.get(url).toPromise();
  }

  getPhaseTypes(workTypeID?: number) {
    const url = '/Field/GetPhaseTypes?workTypeID=' + workTypeID;
    return this.http.get(url).toPromise();
  }

  getTalkingPoints(phaseTypeID?: number, workTypeID?: number) {
    const url = '/Field/GetTalkingPoints?phaseTypeID=' + phaseTypeID + '&workTypeID=' + workTypeID;
    return this.http.get(url).toPromise();
  }

  deleteHuddle(huddleID?: number) {
    return this.http
      .post('/Field/DeleteHuddle', {
        huddleID: huddleID,
      })
      .toPromise();
  }

  getJobs(form: string, searchString: string, JCCo: string) {
    const url = '/Field/GetJobs?form=' + form + '&searchString=' + searchString + '&JCCo=' + JCCo;
    return this.http.get(url).toPromise();
  }

  getPointsByHuddle(huddleID: number) {
    const url = '/Field/GetPointsByHuddle?huddleID=' + huddleID;
    return this.http.get(url).toPromise();
  }

  getDBAttachmentList(moduleID: number, recordID: number, recordGuid: any, onboardingUser: boolean) {
    const url =
      '/Document/GetDBAttachmentList?moduleID=' +
      moduleID +
      '&recordID=' +
      recordID +
      '&recordGuid=' +
      recordGuid +
      '&onboardingUser=' +
      onboardingUser;
    return this.http.get(url).toPromise();
  }

  addEditAttendee(requestObj: any) {
    return this.http
      .post('/Field/AddEditAttendee', {
        attendeeUserKeyID: requestObj.attendeeUserKeyID,
        attendeeSignature: requestObj.attendeeSignature,
        coachUserKeyID: requestObj.coachUserKeyID,
        signatureLatitude: requestObj.signatureLatitude,
        signatureLongitude: requestObj.signatureLongitude,
        huddleID: requestObj.huddleID,
        huddleAttendeeID: requestObj.huddleAttendeeID,
        attendeeSignatureDateStr: requestObj.attendeeSignatureDateStr,
      })
      .toPromise();
  }

  getAttendees(huddleID: number, includeSigData: boolean) {
    const url = '/Field/GetAttendees?huddleID=' + huddleID + '&includeSigData=' + includeSigData;
    return this.http.get(url).toPromise();
  }

  deleteDBAttachment(fileAttachmentID: any) {
    return this.http
      .post('/Document/DeleteDBAttachment', {
        fileAttachmentID: fileAttachmentID,
      })
      .toPromise();
  }
  copyAttendees(fromHuddleID?: number, toHuddleID?: number) {
    return this.http
      .post('/Field/CopyAttendees', {
        fromHuddleID: fromHuddleID,
        toHuddleID: toHuddleID,
      })
      .toPromise();
  }

  removePointFromHuddle(huddleItemID?: number) {
    return this.http
      .post('/Field/RemovePointFromHuddle', {
        huddleItemID: huddleItemID,
      })
      .toPromise();
  }

  addPointToHuddle(huddleID: number, talkingPointID?: number[]) {
    return this.http
      .post('/Field/AddPointToHuddle', {
        huddleID: huddleID,
        talkingPointID: talkingPointID,
      })
      .toPromise();
  }

  removeAttendee(huddleAttendeeID?: number) {
    return this.http
      .post('/Field/RemoveAttendee', {
        huddleAttendeeID: huddleAttendeeID,
      })
      .toPromise();
  }

  deleteTalkingPoint(talkingPointID: number) {
    return this.http
      .post('/Field/DeleteTalkingPoint', {
        talkingPointID: talkingPointID,
      })
      .toPromise();
  }

  addEditTalkingPoint(requestObj: any) {
    return this.http
      .post('/Field/AddEditTalkingPoint', {
        talkingPointID: requestObj.talkingPointID,
        phaseTypeID: requestObj.phaseTypeID,
        talkingPointTitle: requestObj.talkingPointTitle,
        talkingPointBody: requestObj.talkingPointBody,
        isStandard: requestObj.isStandard,
        relatedTask: requestObj.relatedTask,
      })
      .toPromise();
  }

  addEditWorkType(workTypeID: number, workTypeName: string, sortSeq?: number) {
    return this.http
      .post('/Field/AddEditWorkType', {
        workTypeID: workTypeID,
        workTypeName: workTypeName,
        sortSeq: sortSeq,
      })
      .toPromise();
  }

  deleteWorkType(workTypeID?: number) {
    return this.http
      .post('/Field/DeleteWorkType', {
        workTypeID: workTypeID,
      })
      .toPromise();
  }

  addEditPhaseType(phaseTypeID: number, workTypeID: number, phaseTypeName: number, sortSeq?: number) {
    return this.http
      .post('/Field/AddEditPhaseType', {
        phaseTypeID: phaseTypeID,
        workTypeID: workTypeID,
        phaseTypeName: phaseTypeName,
        sortSeq: sortSeq,
      })
      .toPromise();
  }

  deletePhaseType(phaseTypeID?: any) {
    return this.http
      .post('/Field/DeletePhaseType', {
        phaseTypeID: phaseTypeID,
      })
      .toPromise();
  }

  getCostTypes(co: number, contract: string) {
    const url = '/Field/GetCostTypes?co=' + co + '&contract=' + contract;
    return this.http.get(url).toPromise();
  }

  getPhaseByContract(co: number, contract: string) {
    const url = '/Field/GetPhaseByContract?co=' + co + '&contract=' + contract;
    return this.http.get(url).toPromise();
  }

  getAttachmentsDetail(jBINKeyID: number, co: number, contract: string) {
    const url = '/Field/GetAttachmentsDetail?jBINKeyID=' + jBINKeyID + '&co=' + co + '&contract=' + contract;
    return this.http.get(url).toPromise();
  }

  updateAttachmentOrder(fromBillAttachmentID: number, toBillAttachmentID: number, newSequence: number, billType: string) {
    return this.http
      .post('/Field/UpdateAttachmentOrder', {
        fromBillAttachmentID: fromBillAttachmentID,
        toBillAttachmentID: toBillAttachmentID,
        newSequence: newSequence,
        billType: billType,
      })
      .toPromise();
  }

  copyAttachment(attachmentID: number, jBINKeyID: number, moduleID: number) {
    return this.http
      .post('/Field/CopyAttachment', {
        attachmentID: attachmentID,
        jBINKeyID: jBINKeyID,
        moduleID: moduleID,
      })
      .toPromise();
  }

  addBulkAttendees(huddleID: number, pRCo: number, pRCRKeyID: number) {
    return this.http
      .post('/Field/AddBulkAttendees', {
        huddleID: huddleID,
        PRCo: pRCo,
        PRCRKeyID: pRCRKeyID,
      })
      .toPromise();
  }
  addBillAttachment(jBINKeyID: number, attachmentID: number) {
    return this.http
      .post('/Field/AddBillAttachment', {
        jBINKeyID: jBINKeyID,
        attachmentID: attachmentID,
      })
      .toPromise();
  }

  renameAttachment(renamedfile: string, description: string, attachmentID: number, jBINKeyID: number) {
    return this.http
      .post('/Field/RenameAttachment', {
        renamedfile: renamedfile,
        description: description,
        attachmentID: attachmentID,
        jBINKeyID: jBINKeyID,
      })
      .toPromise();
  }

  excludeIncludeAttachment(billAttachmentID: number, isExclude: boolean) {
    return this.http
      .post('/Field/ExcludeIncludeAttachment', {
        billAttachmentID: billAttachmentID,
        isExclude: isExclude,
      })
      .toPromise();
  }

  initializeAttachments(
    jCCo: number,
    contract: string,
    jBINKeyID: number,
    phase: string,
    begMthString: string,
    endMthString: string,
    begDateString: string,
    endDateString: string,
    costType: number,
    sortOrder: string,
    matchJBTMReportOrder: boolean
  ) {
    return this.http
      .post('/Field/InitializeAttachments', {
        jCCo: jCCo,
        contract: contract,
        jBINKeyID: jBINKeyID,
        phase: phase,
        begMthString: begMthString,
        endMthString: endMthString,
        begDateString: begDateString,
        endDateString: endDateString,
        costType: costType,
        sortOrder: sortOrder,
        matchJBTMReportOrder: matchJBTMReportOrder,
      })
      .toPromise();
  }

  compileAndSavePDFData(
    uniqueID: string,
    company: number,
    jBINKeyID: number,
    billNumber: number,
    billMonthString: string,
    begDateString: string,
    endDateString: string,
    inclJCDetail: boolean,
    showArchitect: boolean,
    showOwner: boolean,
    phases: string,
    contract: string,
    breakDetail: boolean,
    restrictByBillNumber: boolean,
    begMthString: string,
    endMthString: string,
    sortOrder: string,
    matchJBTMReportOrder: boolean
  ) {
    return this.http
      .post('/Field/CompileAndSavePDFData', {
        company: company,
        jBINKeyID: jBINKeyID,
        billNumber: billNumber,
        billMonthString: billMonthString,
        begDateString: begDateString,
        endDateString: endDateString,
        inclJCDetail: inclJCDetail,
        showArchitect: showArchitect,
        showOwner: showOwner,
        phases: phases,
        contract: contract,
        breakDetail: breakDetail,
        restrictByBillNumber: restrictByBillNumber,
        begMthString: begMthString,
        endMthString: endMthString,
        sortOrder: sortOrder,
        uniqueID: uniqueID,
        matchJBTMReportOrder: matchJBTMReportOrder,
      })
      .toPromise();
  }

  downloadCompilePDFFile(billMonthString: string, begDateString: string, endDateString: string, contract: string, billNumber: number) {
    const url =
      '/Field/DownloadCompilePDFFile?contract=' +
      contract +
      '&billMonthString=' +
      billMonthString +
      '&begDateString=' +
      begDateString +
      '&endDateString=' +
      endDateString +
      '&billNumber=' +
      billNumber;
    return this.http.get(url).toPromise();
  }

  compileAndSaveZIPData(
    uniqueID: string,
    company: number,
    jBINKeyID: number,
    billNumber: number,
    billMonthString: string,
    begDateString: string,
    endDateString: string,
    inclJCDetail: boolean,
    showArchitect: boolean,
    showOwner: boolean,
    phases: string,
    contract: string,
    breakDetail: boolean,
    restrictByBillNumber: boolean,
    begMthString: string,
    endMthString: string,
    sortOrder: string,
    matchJBTMReportOrder: boolean
  ) {
    return this.http
      .post('/Field/CompileAndSaveZIPData', {
        company: company,
        jBINKeyID: jBINKeyID,
        billNumber: billNumber,
        billMonthString: billMonthString,
        begDateString: begDateString,
        endDateString: endDateString,
        inclJCDetail: inclJCDetail,
        showArchitect: showArchitect,
        showOwner: showOwner,
        phases: phases,
        contract: contract,
        breakDetail: breakDetail,
        restrictByBillNumber: restrictByBillNumber,
        begMthString: begMthString,
        endMthString: endMthString,
        sortOrder: sortOrder,
        uniqueID: uniqueID,
        matchJBTMReportOrder: matchJBTMReportOrder,
      })
      .toPromise();
  }

  downloadCompileZIPFile(billMonthString: string, begDateString: string, endDateString: string, contract: string, billNumber: number) {
    const url =
      '/Field/DownloadCompileZIPFile?contract=' +
      contract +
      '&billMonthString=' +
      billMonthString +
      '&begDateString=' +
      begDateString +
      '&endDateString=' +
      endDateString +
      '&billNumber=' +
      billNumber;
    return this.http.get(url).toPromise();
  }

  getPOVendors(pOCo?: number, searchString?: string) {
    const url = '/Field/GetPOVendors?pOCo=' + pOCo + '&searchString=' + searchString;
    return this.http.get(url).toPromise();
  }

  getPOItemByAPULKeyID(apULKeyID: number) {
    const url = '/Field/GetPOItemByAPULKeyID?aPULKeyID=' + apULKeyID;
    return this.http.get(url).toPromise();
  }

  getJCPhaseDesc(jcco: number, job: any, phase: any, apco: any) {
    const url = '/Field/GetJCPhaseDesc?jcco=' + jcco + '&job=' + job + '&phase=' + phase + '&apco=' + apco;
    return this.http.get(url).toPromise();
  }

  getMaterialInfo(rQCo?: number, material?: string) {
    const url = '/Field/GetMaterialInfo?rQCo=' + rQCo + '&material=' + encodeURIComponent(material);
    return this.http.get(url).toPromise();
  }

  getInvoiceOptions(category: string, aRCo?: number) {
    const url = '/Field/GetInvoiceOptions?category=' + category + '&aRCo=' + aRCo;
    return this.http.get(url).toPromise();
  }

  getInvoiceGrid(groupByField: string, filterString: string) {
    const url = '/Field/GetInvoiceGrid?groupByField=' + groupByField + '&filterString=' + filterString;
    return this.http.get(url).toPromise();
  }

  getInvoiceNotes(invoiceKeyID: number) {
    const url = '/Field/GetInvoiceNotes?invoiceKeyID=' + invoiceKeyID;
    return this.http.get(url).toPromise();
  }

  getInvoiceDetails(invoiceKeyID: number) {
    const url = '/Field/GetInvoiceDetails?invoiceKeyID=' + invoiceKeyID;
    return this.http.get(url).toPromise();
  }

  addInvoiceNote(
    invoiceKeyID: number,
    contact: string,
    dateContactedString: string,
    notes: string,
    summary: string,
    customFields: VistaCustomField[]
  ) {
    return this.http
      .post('/Field/AddInvoiceNote', {
        invoiceKeyID: invoiceKeyID,
        contact: contact,
        dateContactedString: dateContactedString,
        notes: notes,
        summary: summary,
        customFields: customFields,
      })
      .toPromise();
  }

  editInvoiceNote(noteKeyID: bigint, notes: string, summary: string, customFields: VistaCustomField[]) {
    return this.http
      .post('/Field/EditInvoiceNote', {
        noteKeyID: noteKeyID,
        notes: notes,
        summary: summary,
        customFields: customFields,
      })
      .toPromise();
  }

  getPOCompanies(): Promise<POGetCompanies_Result[]> {
    const url = '/Field/GetPOCompanies';
    return this.http.get<POGetCompanies_Result[]>(url).toPromise();
  }

  getPORequisitionGrid(rQCo?: number): Promise<POGetRequisitionGrid_Result[]> {
    const url = '/Field/GetPORequisitionGrid';
    const params = new HttpParams().set('rQCo', rQCo);
    return this.http.get<POGetRequisitionGrid_Result[]>(url, { params: params }).toPromise();
  }

  poRejectRQLines(rQRLKeyIDs: bigint[]): Promise<ApiResponse> {
    return this.http
      .post<ApiResponse>('/Field/PORejectRQLines', {
        rQRLKeyIDs: rQRLKeyIDs,
      })
      .toPromise();
  }

  getDependentLookupByColumnName(columnName: string, countryCode: string) {
    const url = '/Field/GetDependentLookupByColumnName?columnName=' + columnName + '&countryCode=' + countryCode;
    return this.http.get(url).toPromise();
  }

  getPOShippingInfo(jCJMKeyID: number) {
    const url = '/Field/GetPOShippingInfo?jCJMKeyID=' + jCJMKeyID;
    return this.http.get(url).toPromise();
  }

  poReqItemUpdate(rQRLKeyID?: number, units?: number, uC?: number, eCM?: string, totalCost?: number, aPVMKeyID?: number) {
    return this.http
      .post('/Field/POReqItemUpdate', {
        rQRLKeyID: rQRLKeyID,
        units: units,
        uC: uC,
        eCM: eCM,
        totalCost: totalCost,
        aPVMKeyID: aPVMKeyID,
      })
      .toPromise();
  }

  poProcessPOFromRQLines(requestObject: any) {
    return this.http
      .post('/Field/POProcessPOFromRQLines', {
        rQRLKeyIDString: requestObject.rQRLKeyIDString,
        rQRHKeyID: requestObject.rQRHKeyID,
        receiving: requestObject.receiving,
        userPONumber: requestObject.userPONumber,
        jCJMKeyID: requestObject.jCJMKeyID,
        shipAddress: requestObject.shipAddress,
        shipAddress2: requestObject.shipAddress2,
        shipCity: requestObject.shipCity,
        shipState: requestObject.shipState,
        shipZip: requestObject.shipZip,
        attentionTo: requestObject.attentionTo,
        country: requestObject.country,
        pOCo: requestObject.pOCo,
        begPO: requestObject.begPO,
        ct: requestObject.ct,
      })
      .toPromise();
  }

  getPOHistory(queryString: string) {
    const url = '/Field/GetPOHistory?' + queryString;
    return this.http.get(url).toPromise();
  }

  getPOMOHistory(jCJMKeyID: number) {
    const url = '/Field/GetPOMOHistory?jCJMKeyID=' + jCJMKeyID;
    return this.http.get(url).toPromise();
  }

  getPOReqHistory(jCJMKeyID: number, status: number) {
    const url = '/Field/GetPOReqHistory?jCJMKeyID=' + jCJMKeyID + '&status=' + status;
    return this.http.get(url).toPromise();
  }

  getAllPendingPOHeader(jCJMKeyID?: number) {
    const url = '/Field/GetAllPendingPOHeader?jCJMKeyID=' + jCJMKeyID;
    return this.http.get(url).toPromise();
  }

  getPendingPOWorkflow(pOPPKeyID: number, sourceView: string): Promise<POPendingWFGridActionResult[]> {
    const url = '/Field/GetPendingPOWorkflow?pOPPKeyID=' + pOPPKeyID + '&sourceView=' + sourceView;
    return this.http.get<POPendingWFGridActionResult[]>(url).toPromise();
  }

  getPOLookups(queryString: string) {
    const url = '/Field/GetPOLookups?' + queryString;
    return this.http.get<SimpleLookupResult[]>(url).toPromise();
  }

  getPOLookupsForTypeahead(queryString: string) {
    const url = '/Field/GetPOLookups?' + queryString;
    return this.http.get<SimpleLookupResult[]>(url);
  }

  processPOPending(pOPPKeyIDs: number[]): Promise<ProcessPendingPOResponse> {
    return this.http
      .post<ProcessPendingPOResponse>('/Field/ProcessPOPending', {
        pOPPKeyIDs: pOPPKeyIDs,
      })
      .toPromise();
  }

  closePO(pOHDKeyID: any) {
    return this.http
      .post('/Field/ClosePO', {
        pOHDKeyID: pOHDKeyID,
      })
      .toPromise();
  }

  getVPAttachmentList(uniqueAttchID: string) {
    return this.http.get('/Document/GetVPAttachmentList?uniqueAttchID=' + uniqueAttchID).toPromise();
  }

  deleteVPAttachment(attachmentID: number, uniqueAttchID?: any, database?: boolean) {
    return this.http
      .post('/Document/DeleteVPAttachment', {
        attachmentID: attachmentID,
        uniqueAttchID: uniqueAttchID,
        database: database,
      })
      .toPromise();
  }

  updateNotes(note: string, poItem: number, po: string, poCo: number, isHeaderNotes: boolean) {
    return this.http
      .post('/Field/UpdateNotes', {
        note: note,
        poItem: poItem,
        po: po,
        poCo: poCo,
        isHeaderNotes: isHeaderNotes,
      })
      .toPromise();
  }

  getPOVendorsQuicklist(pOCo: number) {
    const url = '/Field/GetPOVendorsQuicklist?pOCo=' + pOCo;
    return this.http.get(url).toPromise();
  }

  getPOUDFieldOptions() {
    const url = '/Field/GetPOUDFieldOptions';
    return this.http.get(url).toPromise();
  }

  getPendingPOItems(pOPPKeyID: number, pOPIKeyID?: number) {
    const url = '/Field/GetPendingPOItems?pOPPKeyID=' + pOPPKeyID + '&pOPIKeyID=' + pOPIKeyID;
    return this.http.get(url).toPromise();
  }

  getPORemainingBudget(jCCo: number, job: string, phase: string, costType?: string) {
    const url = '/Field/GetPORemainingBudget?jCCo=' + jCCo + '&job=' + job + '&phase=' + phase + '&costType=' + costType;
    return this.http.get(url).toPromise();
  }

  getTaxCodeInfo(pOCo: number, taxCode: string) {
    const url = '/Field/GetTaxCodeInfo?pOCo=' + pOCo + '&taxCode=' + taxCode;
    return this.http.get(url).toPromise();
  }

  resetPOWorkflow(pOPPKeyID: number, sourceView: string) {
    return this.http
      .post('/Field/ResetPOWorkflow', {
        pOPPKeyID: pOPPKeyID,
        sourceView: sourceView,
      })
      .toPromise();
  }

  getPendingPOFields(itemType: number, pOCo: number) {
    const url = '/Field/GetPendingPOFields?itemType=' + itemType + '&pOCo=' + pOCo;
    return this.http.get(url).toPromise();
  }

  insertUpdatePendingPOHeader(requestObject: { [key: string]: any }) {
    return this.http.post('/Field/InsertUpdatePendingPOHeader', requestObject).toPromise();
  }

  submitPOPending(requestObj: { [key: string]: any }) {
    return this.http.post('/Field/SubmitPOPending', requestObj).toPromise();
  }

  deletePendingPOItem(pOPIKeyID: number) {
    return this.http
      .post('/Field/DeletePendingPOItem', {
        pOPIKeyID: pOPIKeyID,
      })
      .toPromise();
  }

  getGLDefault(group: any, row: { [key: string]: any }) {
    const url =
      '/Field/GetGLDefault?lineType=' +
      group +
      '&jcco=' +
      row['JCCo'] +
      '&job=' +
      row['Job'] +
      '&jCCType=' +
      row['JCCType'] +
      '&iNCo=' +
      row['INCo'] +
      '&loc=' +
      row['Loc'] +
      '&material=' +
      row['Material'] +
      '&eMCo=' +
      row['EMCo'] +
      '&equip=' +
      row['Equip'] +
      '&wO=' +
      row['WO'] +
      '&costCode=' +
      row['CostCode'] +
      '&eMCType=' +
      row['EMCType'];
    return this.http.get(url).toPromise();
  }

  getVendorAddlAddresses(apvmKeyID: number) {
    const url = '/Field/GetVendorAddlAddresses?apvmKeyID=' + apvmKeyID;
    return this.http.get(url).toPromise();
  }

  insertUpdatePendingPOItem(requestObject: { [key: string]: any }) {
    return this.http.post('/Field/InsertUpdatePendingPOItem', requestObject).toPromise();
  }

  deletePendingPOHeader(pOPPKeyID: number) {
    return this.http.post('/Field/DeletePendingPOHeader', { pOPPKeyID: pOPPKeyID }).toPromise();
  }

  getShippingLocations(poco: number) {
    const url = '/Field/GetShippingLocations?pOCo=' + poco;
    return this.http.get(url).toPromise();
  }

  getCompanyAddress(pOCo?: number) {
    const url = '/Field/GetCompanyAddress?pOCo=' + pOCo;
    return this.http.get(url).toPromise();
  }

  getPOVendorsQuickList(pOCo?: number, jCJMKeyID?: number) {
    const url = '/Field/GetPOVendorsQuicklist?pOCo=' + pOCo + '&jCJMKeyID=' + jCJMKeyID;
    return this.http.get(url).toPromise();
  }

  getPONumber(requestObject: any) {
    return this.http
      .post('/Field/GetPONumber', {
        pOCo: requestObject.pOCo,
        aPVMKeyID: requestObject.aPVMKeyID,
        rQRHKeyID: requestObject.rQRHKeyID,
        pODescription: requestObject.pODescription,
        pONotes: requestObject.pONotes,
        pODateString: requestObject.pODateString,
        pOAmount: requestObject.pOAmount,
        jCJMKeyID: requestObject.jCJMKeyID,
        userPONumber: requestObject.userPONumber,
        uDFieldData: requestObject.uDFieldData,
        recordType: requestObject.recordType,
        sMWorkOrderScopeID: requestObject.sMWorkOrderScopeID,
        sMTripID: requestObject.sMTripID,
        customFields: requestObject.customFields,
      })
      .toPromise();
  }

  getPendingPOHeader(pOPPKeyID: number, jCJMKeyID?: number) {
    const url = '/Field/GetPendingPOHeader?pOPPKeyID=' + pOPPKeyID + '&jCJMKeyID=' + jCJMKeyID;
    return this.http.get(url).toPromise();
  }

  proccessQuickPOBatch(pOHBKeyID: number, aPVMKeyID: number, rQRHKeyID?: number) {
    return this.http
      .post('/Field/ProccessQuickPOBatch', {
        pOHBKeyID: pOHBKeyID,
        aPVMKeyID: aPVMKeyID,
        rQRHKeyID: rQRHKeyID,
      })
      .toPromise();
  }

  copyPO(pOHDKeyID: number) {
    return this.http
      .post('/Field/CopyPO', {
        pOHDKeyID: pOHDKeyID,
      })
      .toPromise();
  }

  runHuddleReportBG(selectedMonth: string, branch: string, selectedType: string): Observable<ReportGenerationApiResponse> {
    const url =
      '/Field/DownloadHuddleDHCompliance?MonthToShow=' +
      moment(selectedMonth).utc().format('MM/DD/YYYY') +
      '&Branch=' +
      branch +
      '&HuddleTypeID=' +
      selectedType;
    return this.http.get<ReportGenerationApiResponse>(url);
  }

  runHuddleTPCComplianceReport(
    selectedBranch: string,
    jobSelected: string,
    selectedWorkType: string,
    startDate: string,
    endDate: string,
    phaseTypeId: string
  ): Observable<ReportGenerationApiResponse> {
    const url =
      '/Field/DownloadHuddleTPCompliance?StartDate=' +
      startDate +
      '&EndDate=' +
      endDate +
      '&Branch=' +
      selectedBranch +
      '&JCJMKeyID=' +
      jobSelected +
      '&WorkTypeID=' +
      selectedWorkType +
      '&PhaseTypeIDString=' +
      phaseTypeId;
    return this.http.get<ReportGenerationApiResponse>(url);
  }

  getPOList(jCJMKeyID?: number, aPVMKeyID?: number, pOCo?: number, searchString: string = null) {
    const url = '/Field/GetPOList?jCJMKeyID=' + jCJMKeyID + '&aPVMKeyID=' + aPVMKeyID + '&pOCo=' + pOCo + '&searchString=' + searchString;
    return this.http.get(url).toPromise();
  }

  getItemsByPO(pOHDKeyID: number) {
    const url = '/Field/GetItemsByPO?pOHDKeyID=' + pOHDKeyID;
    return this.http.get(url).toPromise();
  }

  getPOItemLines(pOHDKeyID?: number, pOITKeyID?: number) {
    const url = '/Field/GetPOItemLines?pOHDKeyID=' + pOHDKeyID + '&pOITKeyID=' + pOITKeyID;
    return this.http.get(url).toPromise();
  }

  submitPOReceipt(
    receipt: any,
    POHDKeyID: number,
    receiptDateString: string,
    notes: string,
    receiverNumber: string,
    batchMonthStr: string
  ) {
    return this.http
      .post('/Field/SubmitPOReceipt', {
        receipt: receipt,
        POHDKeyID: POHDKeyID,
        receiptDateString: receiptDateString,
        notes: notes,
        receiverNumber: receiverNumber,
        batchMonthStr: batchMonthStr,
      })
      .toPromise();
  }

  finalizePOReceipt(pOHDKeyID: number, batchID: number, mthString: string) {
    return this.http
      .post('/Field/FinalizePOReceipt', {
        pOHDKeyID: pOHDKeyID,
        batchID: batchID,
        mthString: mthString,
      })
      .toPromise();
  }

  getPOItemLineDetail(pOItemLineKeyID: number) {
    const url = '/Field/GetPOItemLineDetail?pOItemLineKeyID=' + pOItemLineKeyID;
    return this.http.get(url).toPromise();
  }

  pOReceivingDistribution(pOItemLineKeyID: number, phase: string, newUnits: number) {
    return this.http
      .post('/Field/POReceivingDistribution', {
        pOItemLineKeyID: pOItemLineKeyID,
        phase: phase,
        newUnits: newUnits,
      })
      .toPromise();
  }

  getProgressApprovalGrid() {
    const url = '/Field/GetProgressApprovalGrid';
    return this.http.get(url).toPromise();
  }

  setProgressUpdateItem(
    jCPPKeyID?: number,
    completedThisBatch?: number,
    completedTotalPct?: number,
    notes?: string,
    location?: string,
    subLocation?: string,
    task?: string,
    crew?: string,
    pRCo?: number
  ) {
    return this.http
      .post('/Field/SetProgressUpdateItem', {
        jCPPKeyID: jCPPKeyID,
        completedThisBatch: completedThisBatch,
        completedTotalPct: completedTotalPct,
        notes: notes,
        location: location,
        subLocation: subLocation,
        task: task,
        crew: crew,
        pRCo: pRCo,
      })
      .toPromise();
  }

  getProgressHistory() {
    const url = '/Field/GetProgressHistory';
    return this.http.get(url).toPromise();
  }

  getProgressLoad(
    jCJMKeyID?: number,
    actualDateStr?: string,
    batchID?: number,
    mthStr?: string,
    hideResults?: boolean,
    testForDuplicates?: boolean
  ) {
    return this.http
      .post('/Field/GetProgressLoad', {
        jCJMKeyID: jCJMKeyID,
        actualDateStr: actualDateStr,
        batchID: batchID,
        mthStr: mthStr,
        hideResults: hideResults,
        testForDuplicates: testForDuplicates,
      })
      .toPromise();
  }

  getCrews(pRCo?: number) {
    const url = '/Field/GetCrews?pRCo=' + pRCo;
    return this.http.get(url).toPromise();
  }

  setProgressUpdateItemMulti(requestObj: any) {
    return this.http.post('/Field/SetProgressUpdateItemMulti', requestObj).toPromise();
  }

  getProgressCancel(jCJMKeyID?: number, actualDateStr?: string, batchID?: number, mthStr?: string) {
    return this.http
      .post('/Field/GetProgressCancel', {
        jCJMKeyID: jCJMKeyID,
        actualDateStr: actualDateStr,
        batchID: batchID,
        mthStr: mthStr,
      })
      .toPromise();
  }

  getProgressFinalize(
    jCJMKeyID?: number,
    actualDateStr?: string,
    batchID?: number,
    mthStr?: string,
    signatureDataStr?: string,
    longitude?: number,
    latitude?: number
  ) {
    return this.http
      .post('/Field/GetProgressFinalize', {
        jCJMKeyID: jCJMKeyID,
        actualDateStr: actualDateStr,
        batchID: batchID,
        mthStr: mthStr,
        signatureDataStr: signatureDataStr,
        longitude: longitude,
        latitude: latitude,
      })
      .toPromise();
  }

  getPORestrictionSetting() {
    const url = '/Field/GetPORestrictionSetting';
    return this.http.get(url).toPromise();
  }

  getPOReqHeader(rQRHKeyID: number) {
    const url = '/Field/GetPOReqHeader?rQRHKeyID=' + rQRHKeyID;
    return this.http.get(url).toPromise();
  }

  getPOReqItems(rQRHKeyID: number) {
    const url = '/Field/GetPOReqItems?rQRHKeyID=' + rQRHKeyID;
    return this.http.get(url).toPromise();
  }

  getPOReqFields(pOReqLineType: number, pOCo: number) {
    const url = '/Field/GetPOReqFields?pOReqLineType=' + pOReqLineType + '&pOCo=' + pOCo;
    return this.http.get(url).toPromise();
  }

  insertUpdatePOReqHeader(requestObject: any) {
    return this.http
      .post('/Field/InsertUpdatePOReqHeader', {
        rQRHKeyID: requestObject.rQRHKeyID,
        rQCo: requestObject.rQCo,
        requestDateString: requestObject.requestDateString,
        headerDescription: requestObject.headerDescription,
        headerNotes: requestObject.headerNotes,
        requestor: requestObject.requestor,
        aPVMKeyID: requestObject.aPVMKeyID,
        receiving: requestObject.receiving,
        recordType: requestObject.recordType,
        jCJMKeyID: requestObject.jCJMKeyID,
        uDFieldData: requestObject.uDFieldData,
        maskReturn: requestObject.maskReturn,
      })
      .toPromise();
  }

  pOProcessMOFromRQLines(rQRHKeyID: number) {
    return this.http
      .post('/Field/POProcessMOFromRQLines', {
        rQRHKeyID: rQRHKeyID,
      })
      .toPromise();
  }

  deletePOReq(rQRHKeyID?: number, rQRLKeyID?: number) {
    return this.http
      .post('/Field/DeletePOReq', {
        rQRHKeyID: rQRHKeyID,
        rQRLKeyID: rQRLKeyID,
      })
      .toPromise();
  }

  insertUpdatePOReqItems(requestObjects: PORequestItem[]): Promise<ApiDataResponse<POReqInsertUpdateItemsResult[]>> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.http
      .post<ApiDataResponse<POReqInsertUpdateItemsResult[]>>('/Field/InsertUpdatePOReqItems',{ requestObjects: requestObjects }, { headers})
      .toPromise();
  }

  getJobsForPO(path: string) {
    return this.http.get(path);
  }

  processPMPO(pMCo: any, project: string, pO: string): Promise<ApiResponse> {
    return this.http
      .post<ApiResponse>('/Field/ProcessPMPO', {
        pMCo: pMCo,
        project: project,
        pO: pO,
      })
      .toPromise();
  }

  getInvoiceNotesAndDetail(invoiceKeyID: number, includeDetails = false) {
    const httpParams = new HttpParams().set('invoiceKeyID', invoiceKeyID).set('includeDetails', includeDetails);
    const url = '/Field/GetInvoiceNotesAndDetails';
    return this.http.get<GetInvoiceNotes_Response>(url, { params: httpParams }).toPromise();
  }

  updateRqBuyer(rQRLKeyIDs: bigint[], buyerUserKeyID: number | null): Promise<ApiResponse> {
    return this.http
      .post<ApiResponse>('/Field/UpdateRQBuyer', {
        rqrlKeyIDs: rQRLKeyIDs,
        buyerUserKeyID: buyerUserKeyID,
      })
      .toPromise();
  }

  getPurchasingAdmins(): Promise<SimpleLookupResult[]> {
    const url = '/Field/GetPurchasingAdmins';
    return this.http.get<SimpleLookupResult[]>(url).toPromise();
  }
}

export interface ProcessPendingPOResponse extends ApiResponse {
  response: {
    exceptionCount: number;
    exceptionList: string[];
  };
}

export interface POPendingWFGridActionResult {
  POItem?: number | null;
  Step?: number | null;
  UserRole: string;
  UserName: string;
  ApprovalLimit?: number | null;
  RequiresApproval?: boolean | null;
  Status?: number | null;
  POItemDescription: string;
  Comments: string;
}

export interface PORequestItem extends POReqInsertUpdateItemsRequest {
  ExpDate: string | null;
  ReqDate: string | null;
}
